import {
  Component,
  inject,
  type OnDestroy,
  type OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { FinoSelectModule, type SelectComponent } from '@fino-ui/select';
import { FinoTranslationModule } from '@fino-ui/translation';
import Keycloak from 'keycloak-js';
import { map } from 'lodash';
import { LocalStorage } from 'ngx-webstorage';
import { finalize, interval, Observable, Subject, takeUntil } from 'rxjs';
import { TicketOverviewService } from './ticket-overview.service';
import { TicketOverviewChartComponent } from './ticket-overview-chart/ticket-overview-chart.component';
import { TicketTableComponent } from './ticket-table/ticket-table.component';

const INTERVAL_REFRESH = 1000 * 60 * 2;

@Component({
  selector: 'app-ticket-overview',
  templateUrl: './ticket-overview.component.html',
  styleUrls: ['./ticket-overview.component.sass'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    FinoCommonModule,
    FinoSelectModule,
    TicketOverviewChartComponent,
    FinoIconModule,
    FinoInputModule,
    TicketTableComponent,
    FinoTranslationModule,
  ],
})
export class TicketOverviewComponent implements OnInit, OnDestroy {
  private readonly ticketOverviewService = inject(TicketOverviewService);

  private readonly authService = inject(Keycloak);

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  showBankList = false;

  private onDestroy$ = new Subject<void>();

  public searchForm: SubmittableForm = new SubmittableForm({
    keyword: new FormControl(''),
    showOnlyRemindersPaused: new FormControl(),
    showOnlyOpenStandingOrders: new FormControl(),
  });

  public bankForm: SubmittableForm = new SubmittableForm({
    bank: new FormControl(''),
  });

  get hasTenants() {
    return this.tenants().length > 0;
  }

  @ViewChild('tenantSelect')
  tenantSelect: SelectComponent | undefined;

  public blacklistStatus: string[] = [];
  public tenants = signal<any>([]);
  public tickets = signal<any[]>([]);
  public pagination = signal<any>({});
  public statistic = signal<any>({});
  public sortMode: boolean = false;
  @LocalStorage()
  public sortOrder: 'ASC' | 'DESC' | undefined;
  @LocalStorage()
  public filterProperty: string | undefined;
  @LocalStorage()
  public hideChart: boolean | undefined;

  public searchTerm: string = '';
  public showOnlyRemindersPaused: boolean = false;
  public showOnlyOpenStandingOrders: boolean = false;

  public loading = signal(false);
  public currentPage = signal(1);

  public interval: Observable<number> = new Observable();
  public organization: string;

  ngOnInit() {
    this.interval = interval(INTERVAL_REFRESH).pipe(takeUntil(this.onDestroy$));
    this.interval.subscribe(() => this.fetchTickets(this.organization, {}));

    const userProfile = this.authService.tokenParsed;
    this.organization = userProfile?.organization;

    this.fetchTickets(this.organization, {
      term: this.searchTerm,
      sortField: this.filterProperty,
      sortOrder: this.sortOrder,
    });

    this.ticketOverviewService
      .fetchTenants(this.organization)
      .subscribe((tenants) => {
        this.tenants.set(map(tenants, (t) => this.adjustTenant(t)));
      });

    this.searchForm.valueChanges.subscribe((value) => {
      this.searchTerm = value.keyword;
      this.showOnlyRemindersPaused = value.showOnlyRemindersPaused;
      this.showOnlyOpenStandingOrders = value.showOnlyOpenStandingOrders;

      this.currentPage.set(1);
      this.fetchTickets(this.organization, value.keyword);
    });
  }

  get lastPage() {
    const pagination = this.pagination();
    return pagination && Math.ceil(pagination.total / pagination.size);
  }

  get hasTickets() {
    return this.tickets().length > 0;
  }

  nextPage() {
    this.goToPage(this.currentPage() + 1);
  }

  adjustTenant(tenant: any) {
    tenant.urlWithoutProtocol = tenant.url.replace('http://', '');
    return tenant;
  }

  toggleBankList() {
    this.showBankList = !this.showBankList;
    setTimeout(() => {
      this.tenantSelect?.focus();
    });
  }

  selectBank(tenant: any) {
    this.ticketOverviewService
      .getTenantLink(tenant.name, this.organization)
      .subscribe((res: any) => this.openKWH(res));
  }

  openKWH(response: any, id = '') {
    this.ticketOverviewService.openKWH(response, id);
  }

  previousPage() {
    this.goToPage(this.currentPage() - 1);
  }

  goToPage(page: number) {
    this.currentPage.set(page);
    this.fetchTickets(this.organization, {
      term: this.searchTerm,
      sortField: this.filterProperty,
      sortOrder: this.sortOrder,
      page: this.currentPage(),
    });
  }

  fetchTickets(
    organization = this.organization,
    {
      term = '',
      sortField = '',
      sortOrder = 'DESC',
      page = 1,
      ignoreStatistics = false,
    }: {
      term?: string;
      sortField?: string;
      sortOrder?: 'ASC' | 'DESC';
      page?: number;
      ignoreStatistics?: boolean;
    },
  ) {
    this.filterProperty = sortField;
    this.sortOrder = sortOrder;
    this.loading.set(true);
    this.ticketOverviewService
      .fetchTickets(
        organization,
        term,
        sortField,
        sortOrder,
        page,
        this.blacklistStatus,
        this.showOnlyRemindersPaused,
        this.showOnlyOpenStandingOrders,
      )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((tickets: any) => {
        this.tickets.set(tickets.data);
        this.pagination.set(tickets.page);
        // since statistic response is always same dont mess up
        // when applying blacklist ticket filter
        if (!ignoreStatistics) {
          this.statistic.set(tickets.totalCounts);
        }
      });
  }

  onLegendClick($event: string[]) {
    this.blacklistStatus = $event;
    this.fetchTickets(this.organization, { ignoreStatistics: true });
  }

  toggleChart() {
    this.hideChart = !this.hideChart;
  }

  sortByField(event: any) {
    this.currentPage.set(1);
    this.fetchTickets(this.organization, {
      term: this.searchTerm,
      sortField: event.sortField,
      sortOrder: event.sortBy,
    });
  }
}
